import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import {cl} from '../../components/utils/utils'
import './C18ComboBox02.css'

function normalizeOpts(raw) {
  if (!Array.isArray(raw)) return [];
  return raw.map((item, i) => {
    if (item == null) return {t: "", key: i};
    if (typeof item === "string" || typeof item === "number") {
      const s = String(item);
      return {t: s, key: s};
    }
    const t = item.t != null ? String(item.t) : "";
    return {t, key: item.key != null ? item.key : `${t}-${i}`};
  });
}

function indexOfValue(list, want) {
  const s = want == null ? "" : String(want);
  if (!s) return -1;
  let hi = list.findIndex((o) => o.t === s);
  if (hi < 0) {
    const lower = s.toLowerCase();
    hi = list.findIndex((o) => o.t.toLowerCase() === lower);
  }
  return hi;
}

const C18ComboBox02 = forwardRef(function C18ComboBox02(props, ref) {
  const pa = props.parms || {};
  const valueName = pa.valueName;
  const label = pa.label != null ? pa.label : props.label;
  const disabled = !!(pa.disabled != null ? pa.disabled : props.disabled);
  const style = props.style || pa.style;
  const className = [props.className || pa.className, "custom-select", "custom-combobox"]
    .filter(Boolean)
    .join(" ");

  const inputRef = useRef(null);
  const rootRef = useRef(null);
  const itemRefs = useRef([]);

  const [itemsOverride, setItemsOverride] = useState(null);
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(-1);
  const [textOverride, setTextOverride] = useState(null);
  const originalRef = useRef("");

  const propOpts = useMemo(
    () => normalizeOpts(itemsOverride != null ? itemsOverride : (pa.opts || props.items || [])),
    [itemsOverride, pa.opts, props.items]
  );

  const propValue =
    valueName != null && pa[valueName] != null
      ? pa[valueName]
      : props.value != null
        ? props.value
        : pa.default != null
          ? pa.default
          : "";

  const displayFromProps = propValue == null ? "" : String(propValue);

  const text = textOverride != null ? textOverride : displayFromProps;

  useEffect(() => {
    setTextOverride(null);
  }, [displayFromProps]);

  const filtered = useMemo(() => {
    const q = String(text || "").trim().toLowerCase();
    if (!q) return propOpts;
    return propOpts.filter((o) => o.t.toLowerCase().includes(q));
  }, [propOpts, text]);

  useEffect(() => {
    if (highlight >= filtered.length) {
      setHighlight(filtered.length ? filtered.length - 1 : -1);
    }
  }, [filtered, highlight]);

  const onChangeParm = pa.onChange;
  const onChangeProp = props.onChange;

  const fireChange = useCallback(
    (str) => {
      const s = str == null ? "" : String(str);
      if (typeof onChangeParm === "function") {
        const val = {};
        if (valueName != null) {
          val[valueName] = s;
        } else {
          val.value = s;
        }
        onChangeParm("upd", val);
      } else if (typeof onChangeProp === "function") {
        onChangeProp(s);
      }
    },
    [onChangeParm, onChangeProp, valueName]
  );

  const commitText = useCallback(
    (str, {close} = {close: true}) => {
      const s = str == null ? "" : String(str);
      setTextOverride(s);
      fireChange(s);
      if (close) {
        setOpen(false);
        setHighlight(-1);
      }
    },
    [fireChange]
  );

  const openPopup = useCallback(
    (nextText, selectText) => {
      if (disabled) return;
      if (!open) {
        originalRef.current = text == null ? "" : String(text);
      }
      const t = nextText != null ? nextText : text;
      const q = String(t || "").trim().toLowerCase();
      const list = !q
        ? propOpts
        : propOpts.filter((o) => o.t.toLowerCase().includes(q));
      const want = selectText != null ? selectText : t;
      let hi = indexOfValue(list, want);
      if (hi < 0 && list.length) hi = 0;
      setHighlight(hi);
      setOpen(true);
    },
    [disabled, text, propOpts, open]
  );

  const revertToOriginal = useCallback(() => {
    const orig = originalRef.current == null ? "" : String(originalRef.current);
    setTextOverride(orig);
    fireChange(orig);
    setOpen(false);
    setHighlight(-1);
  }, [fireChange]);

  const selectIndex = useCallback(
    (idx) => {
      const item = filtered[idx];
      if (!item) return;
      commitText(item.t, {close: true});
    },
    [filtered, commitText]
  );

  useImperativeHandle(
    ref,
    () => ({
      setItems(items) {
        setItemsOverride(items != null ? items : []);
      },
      setValue(value) {
        setTextOverride(value == null ? "" : String(value));
      },
      getValue() {
        return text == null ? "" : String(text);
      },
      focus() {
        if (inputRef.current) inputRef.current.focus();
      },
    }),
    [text]
  );

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) {
        revertToOriginal();
      }
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open, revertToOriginal]);

  useEffect(() => {
    if (!open || highlight < 0) return;
    const el = itemRefs.current[highlight];
    if (el && typeof el.scrollIntoView === "function") {
      el.scrollIntoView({block: "nearest"});
    }
  }, [open, highlight, filtered]);

  const onInputChange = (e) => {
    const v = e.currentTarget.value;
    setTextOverride(v);
    fireChange(v);
    openPopup(v);
  };

  const onArrowClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (disabled) return;
    if (open) {
      revertToOriginal();
      return;
    }
    const current = text;
    setTextOverride("");
    if (inputRef.current) inputRef.current.focus();
    openPopup("", current);
  };

  const onKeyDown = (e) => {
    if (disabled) return;
    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        if (!open) {
          openPopup(text);
        } else if (filtered.length) {
          setHighlight((h) => {
            if (h < 0) return 0;
            return Math.min(filtered.length - 1, h + 1);
          });
        }
        break;
      case "ArrowUp":
        e.preventDefault();
        if (open && filtered.length) {
          setHighlight((h) => {
            if (h < 0) return filtered.length - 1;
            return Math.max(0, h - 1);
          });
        }
        break;
      case "Enter":
        if (open && highlight >= 0 && filtered[highlight]) {
          e.preventDefault();
          selectIndex(highlight);
        } else if (open) {
          e.preventDefault();
          // empty text: keep previous value instead of saving blank
          if (!String(text || "").trim()) {
            revertToOriginal();
          } else {
            commitText(text, {close: true});
          }
        }
        break;
      case "Escape":
        if (open) {
          e.preventDefault();
          revertToOriginal();
        }
        break;
      case "Tab":
        if (open) {
          if (!String(text || "").trim()) {
            revertToOriginal();
          } else {
            commitText(text, {close: true});
          }
        }
        break;
      default:
        break;
    }
  };

  const inputId = valueName || props.id || "c18-combobox";

//   cl({text, open, highlight, filtered: filtered.length})

  return (
    <>
      {label != null && label !== "" && (
        <label htmlFor={inputId}>{label}</label>
      )}
      <span
        ref={rootRef}
        className={className}
        style={style}
      >
        <input
          ref={inputRef}
          id={inputId}
          type="text"
          className="combo-input"
          value={text}
          disabled={disabled}
          autoComplete="off"
          onChange={onInputChange}
          onKeyDown={onKeyDown}
        />
        <button
          type="button"
          className="combo-arrow"
          tabIndex={-1}
          disabled={disabled}
          aria-label="Open list"
          onMouseDown={(e) => e.preventDefault()}
          onClick={onArrowClick}
        >
          <span className="material-icons down-arrow" translate="no">
            keyboard_arrow_down
          </span>
        </button>
        {open && (
          <div className="combo-popup" role="listbox">
            {filtered.length === 0 ? (
              <div className="list-item combo-empty">No matches</div>
            ) : (
              filtered.map((o, i) => (
                <div
                  key={o.key}
                  ref={(el) => {
                    itemRefs.current[i] = el;
                  }}
                  role="option"
                  className={`list-item${i === highlight ? " selected" : ""}`}
                  onMouseEnter={() => setHighlight(i)}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => selectIndex(i)}
                >
                  {o.t}
                </div>
              ))
            )}
          </div>
        )}
      </span>
    </>
  );
});

export default C18ComboBox02;
